import { z } from "zod";

/**
 * T13-13: the admin's CSP has `script-src 'self'` (no 'unsafe-eval'). Zod 4 probes `Function("")`
 * once to decide whether it may JIT-compile object parsers — under that CSP the probe is blocked
 * and reported as a violation on every page (it then falls back to the plain parser). `jitless`
 * skips the probe; it is global, so it is set here, in the first module that parses anything.
 */
z.config({ jitless: true });

const flag = z
  .string()
  .optional()
  .transform((v) => v === "true" || v === "1");

export const envSchema = z.object({
  /** Gateway prefix for admin APIs (Caddy routes `/api/v1/admin/*` → services, design/07). */
  VITE_API_BASE_URL: z.string().default("/api/v1"),
  /** Where the "Open POS" button points (design/04 §0 home buttons). */
  VITE_POS_URL: z.string().default("http://localhost:5174"),
  /** Dev only: accept any credentials without identity-service (T11-02 uses the real API otherwise). */
  VITE_AUTH_DEV_BYPASS: flag,
  /** Storefront shown in the theme editor preview iframe (T09-21), e.g. https://shop.example. */
  VITE_STOREFRONT_URL: z.string().default("http://localhost:3000"),
  /** Extra origins the editor accepts preview messages from (comma separated; T09-21). */
  VITE_PREVIEW_ORIGINS: z.string().default(""),
  /** Staff without `cms.publish` can send "please publish" to the managers' inbox (T09-25 / T12). */
  VITE_PUBLISH_REQUESTS: flag,
  /** GlitchTip (Sentry protocol) DSN of the admin project — empty = errors stay in the console (T11-06). */
  VITE_GLITCHTIP_DSN: z.string().default(""),
  /** Serve the dev component gallery (/_dev/components) in a production build (e2e on preview). */
  VITE_COMPONENT_GALLERY: flag,
  /** Release tag sent with error reports (matches the uploaded source maps — T00-13). */
  VITE_RELEASE: z.string().default("dev"),
  /**
   * Open card-data sources the owner has licensed for "import a set" (T11-14, comma separated:
   * `pokemontcg`, `tcgplayer`). Empty = the button is hidden (catalog answers 501 until then).
   */
  VITE_CARD_IMPORT_SOURCES: z.string().default(""),
  /** Owner links on Settings → Background tasks (T11-46): Uptime Kuma + GlitchTip pages (empty = hidden). */
  VITE_UPTIME_URL: z.string().default(""),
  VITE_GLITCHTIP_URL: z.string().default(""),
  /** Where "Report a problem or idea" e-mails go when GlitchTip is not set up (T11-47; empty = hidden). */
  VITE_SUPPORT_EMAIL: z.string().default(""),
});

export type Env = z.infer<typeof envSchema>;

export function parseEnv(raw: Record<string, string | boolean | undefined>): Env {
  const result = envSchema.safeParse(raw);
  if (!result.success) {
    const lines = result.error.issues.map((i) => `  ${i.path.join(".")}: ${i.message}`);
    throw new Error(`Invalid admin environment:\n${lines.join("\n")}`);
  }
  return result.data;
}

/** `import.meta.env` (undefined when a module is imported by Node tooling, e.g. Playwright specs). */
const viteEnv: Partial<ImportMetaEnv> = (import.meta.env as ImportMetaEnv | undefined) ?? {};

export const env: Env = parseEnv({
  VITE_API_BASE_URL: viteEnv.VITE_API_BASE_URL,
  VITE_POS_URL: viteEnv.VITE_POS_URL,
  VITE_AUTH_DEV_BYPASS: viteEnv.VITE_AUTH_DEV_BYPASS,
  VITE_STOREFRONT_URL: viteEnv.VITE_STOREFRONT_URL,
  VITE_PREVIEW_ORIGINS: viteEnv.VITE_PREVIEW_ORIGINS,
  VITE_PUBLISH_REQUESTS: viteEnv.VITE_PUBLISH_REQUESTS,
  VITE_GLITCHTIP_DSN: viteEnv.VITE_GLITCHTIP_DSN,
  VITE_RELEASE: viteEnv.VITE_RELEASE,
  VITE_COMPONENT_GALLERY: viteEnv.VITE_COMPONENT_GALLERY,
  VITE_CARD_IMPORT_SOURCES: viteEnv.VITE_CARD_IMPORT_SOURCES,
  VITE_UPTIME_URL: viteEnv.VITE_UPTIME_URL,
  VITE_GLITCHTIP_URL: viteEnv.VITE_GLITCHTIP_URL,
  VITE_SUPPORT_EMAIL: viteEnv.VITE_SUPPORT_EMAIL,
});

export const isDev: boolean = viteEnv.DEV ?? false;
